import moment from 'moment/moment'
import React from 'react'

import { EuiPanel, EuiFlexGroup, EuiFlexItem, EuiStat, EuiTitle, EuiSpacer, EuiSuperSelect, EuiDatePickerRange, EuiDatePicker, EuiLoadingSpinner } from '@elastic/eui'

import { ReportCampaign } from 'api/interfaces'
import { filterCampaignDataByViews } from 'features/reports/campaign-reports/reportUtils'
import { formatCurrency, formatNumber, formatPercentage } from 'utils/Formatters'

import { TitleComponent } from './FormattingComponents'

interface CampaignSummaryPanelsProps {
  results?: ReportCampaign[]
  onDateChange: (startDate: moment.Moment, endDate: moment.Moment) => void
  onDateRangeSelect: (value: string) => void
  startDate: moment.Moment
  endDate: moment.Moment
  isLoading: boolean
}

const dateRangeOptions = [
  { value: 'last_3', inputDisplay: 'Last 3 Days' },
  { value: 'last_7', inputDisplay: 'Last 7 Days' },
  { value: 'week', inputDisplay: 'This Week (so far)' },
  { value: 'last_30', inputDisplay: 'Last 30 Days' },
  { value: 'month', inputDisplay: 'This Month (so far)' },
  { value: 'last_month', inputDisplay: 'Last Month' },
  { value: 'last_90', inputDisplay: 'Last 90 Days' },
  { value: 'custom', inputDisplay: 'Custom Range' }
]

const CampaignSummaryPanels: React.FC<CampaignSummaryPanelsProps> = ({ results, onDateChange, onDateRangeSelect, startDate, endDate, isLoading }) => {
  const [selectedRange, setSelectedRange] = React.useState('last_30')

  const title = 'Campaign Summary'

  const handleSuperSelectChange = (value: string) => {
    setSelectedRange(value)
    onDateRangeSelect(value)
  }

  const handleDateChange = (start: moment.Moment, end: moment.Moment) => {
    const minAllowedStartDate = moment().subtract(90, 'days')

    let adjustedStart = start
    let adjustedEnd = end

    if (start.isBefore(minAllowedStartDate)) {
      adjustedStart = minAllowedStartDate
    }

    if (end.isAfter(moment())) {
      adjustedEnd = moment()
    }

    onDateChange(adjustedStart, adjustedEnd)
  }

  const filteredResults = filterCampaignDataByViews(results)

  const aggregatedMetrics = filteredResults.reduce(
    (acc, result) => {
      acc.views += result.views
      acc.clicks += result.clicks
      acc.spend += result.spend
      return acc
    },
    { views: 0, clicks: 0, spend: 0 }
  )

  const ctr = aggregatedMetrics.views > 0 ? aggregatedMetrics.clicks / aggregatedMetrics.views : 0
  const cpm = aggregatedMetrics.views > 0 ? (aggregatedMetrics.spend / aggregatedMetrics.views) * 1000 : 0
  const avgDailySpend = filteredResults.length > 0 ? aggregatedMetrics.spend / filteredResults.length : 0

  const isInternetCampaign = filteredResults.some(result => result.campaignType === 'Internet')

  return (
    <>
      <EuiFlexGroup justifyContent='spaceBetween' alignItems='center'>
        <EuiFlexItem grow={false}>
          <TitleComponent title={title} />
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiFlexGroup>
            <EuiFlexItem grow={false}>
              <EuiSuperSelect options={dateRangeOptions} valueOfSelected={selectedRange} onChange={handleSuperSelectChange} aria-label='Select date range' style={{ borderRadius: 0, minWidth: 200 }} />
            </EuiFlexItem>
            {selectedRange === 'custom' && (
              <EuiFlexItem grow={false}>
                <EuiDatePickerRange
                  fullWidth={false}
                  startDateControl={<EuiDatePicker selected={startDate} onChange={date => handleDateChange(date!, endDate)} startDate={startDate} endDate={endDate} isInvalid={startDate > endDate} aria-label='Start date' showTimeSelect={false} className='whiteCalendarInput' minDate={moment().subtract(90, 'days')} maxDate={moment()} />}
                  endDateControl={<EuiDatePicker selected={endDate} onChange={date => handleDateChange(startDate, date!)} startDate={startDate} endDate={endDate} isInvalid={startDate > endDate} aria-label='End date' showTimeSelect={false} className='whiteCalendarInput' minDate={startDate} maxDate={moment()} />}
                />
              </EuiFlexItem>
            )}
          </EuiFlexGroup>
        </EuiFlexItem>
      </EuiFlexGroup>
      <EuiSpacer size='m' />
      {filteredResults.length <= 0 ? (
        <EuiFlexGroup>
          <EuiFlexItem grow={false}>
            {isLoading ? (
              <EuiLoadingSpinner />
            ) : (
              <EuiTitle size='xs'>
                <span style={{ fontWeight: 400 }}>There is no campaign data for the selected period.</span>
              </EuiTitle>
            )}
          </EuiFlexItem>
        </EuiFlexGroup>
      ) : (
        <EuiFlexGroup gutterSize='l'>
          <EuiFlexItem>
            <EuiPanel paddingSize='m'>
              <EuiStat
                title={formatNumber(aggregatedMetrics.views, 0, 0)}
                description={
                  <EuiTitle size='s'>
                    <span style={{ fontWeight: 400 }}>Total Views</span>
                  </EuiTitle>
                }
                titleSize='l'
                textAlign='center'
                isLoading={isLoading}
              />
            </EuiPanel>
          </EuiFlexItem>

          <EuiFlexItem>
            <EuiPanel paddingSize='m'>
              <EuiStat
                title={formatCurrency(avgDailySpend)}
                description={
                  <EuiTitle size='s'>
                    <span style={{ fontWeight: 400 }}>Avg. Daily Spend</span>
                  </EuiTitle>
                }
                titleSize='l'
                textAlign='center'
                isLoading={isLoading}
              />
            </EuiPanel>
          </EuiFlexItem>

          {isInternetCampaign && (
            <>
              <EuiFlexItem>
                <EuiPanel paddingSize='m'>
                  <EuiStat
                    title={formatPercentage(ctr, 3, 3)}
                    description={
                      <EuiTitle size='s'>
                        <span style={{ fontWeight: 400 }}>CTR %</span>
                      </EuiTitle>
                    }
                    titleSize='l'
                    textAlign='center'
                    isLoading={isLoading}
                  />
                </EuiPanel>
              </EuiFlexItem>
            </>
          )}

          <EuiFlexItem>
            <EuiPanel paddingSize='m'>
              <EuiStat
                title={formatCurrency(cpm)}
                description={
                  <EuiTitle size='s'>
                    <span style={{ fontWeight: 400 }}>CPM</span>
                  </EuiTitle>
                }
                titleSize='l'
                textAlign='center'
                isLoading={isLoading}
              />
            </EuiPanel>
          </EuiFlexItem>
        </EuiFlexGroup>
      )}
    </>
  )
}

export default CampaignSummaryPanels
